import { PermissionEnum } from "@altertec_gparn/lib";
import { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw, Router } from "vue-router";
import { SecurityManager } from "@/core/common/security/security.manager";
import { UserDto } from "@/core/features/users/user.dto";
import { canAccessPlanningBoard } from "@/modules/scheduler/planning-board/can-access-planning-board";

export function findSectionRootRoute(
  matched: RouteLocationNormalized["matched"]
): RouteLocationNormalized["matched"][number] | null {
  for (let index = matched.length - 1; index >= 0; index--) {
    const route = matched[index];
    if (route.meta?.redirects && route.meta?.sectionAccess) {
      return route;
    }
  }

  return null;
}

export function buildChildRouteLocation(sectionRootPath: string, child: RouteRecordRaw): RouteLocationRaw {
  if (child.name) {
    return { name: child.name as string };
  }

  if (child.path.startsWith("/")) {
    return { path: child.path };
  }

  const normalizedSectionPath = sectionRootPath.replace(/\/$/, "");
  const normalizedChildPath = child.path.replace(/^\//, "");
  return { path: `${normalizedSectionPath}/${normalizedChildPath}` };
}

export function getSectionFirstAllowedRoute(
  router: Router,
  securityManager: SecurityManager,
  sectionRootRoute: RouteLocationNormalized["matched"][number],
  me: UserDto
): RouteLocationRaw | null {
  const parentRecord = router.getRoutes().find((route) => route.name === sectionRootRoute.name);
  if (!parentRecord?.children?.length) {
    return null;
  }

  const allowedChild = parentRecord.children.find(
    (route) =>
      route.meta?.permissions &&
      securityManager.hasPermission(route.meta.permissions as PermissionEnum[], me) &&
      (!route.meta?.requiresPlanningBoardBusinessUnit || canAccessPlanningBoard(me))
  );

  if (!allowedChild) {
    return null;
  }

  return buildChildRouteLocation(sectionRootRoute.path, allowedChild);
}

export function resolveSectionNavigationTarget(
  router: Router,
  securityManager: SecurityManager,
  sectionPath: string,
  me: UserDto
): RouteLocationRaw {
  const resolvedSection = router.resolve({ path: sectionPath });
  const sectionRootRoute = findSectionRootRoute(resolvedSection.matched);

  if (!sectionRootRoute) {
    return { path: sectionPath };
  }

  const firstAllowedRoute = getSectionFirstAllowedRoute(router, securityManager, sectionRootRoute, me);
  return firstAllowedRoute ?? { path: sectionPath };
}

export function isSameResolvedPath(router: Router, currentPath: string, location: RouteLocationRaw): boolean {
  return router.resolve(location).path === currentPath;
}
